'use client';

import { useMemo, useRef, useState } from 'react';
import { parseCidrInput, subnet } from '@/lib/browser-subnet';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export default function BrowserSubnetCalculator() {
  const [address, setAddress] = useState('192.168.1.10');
  const [prefix, setPrefix] = useState('24');
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage('subnet-calculator', '').catch(() => undefined);
  };

  const { result, error } = useMemo(() => {
    try {
      const [a, p] = parseCidrInput(address, prefix);
      return { result: subnet(a, p), error: null };
    } catch (e) {
      return { result: null, error: e instanceof Error ? e.message : 'Check the address.' };
    }
  }, [address, prefix]);

  const rows: Array<[string, string]> = result
    ? [
        ['Network address', `${result.network}/${result.cidr}`],
        ['Subnet mask', result.mask],
        ['Wildcard mask', result.wildcard],
        ['Broadcast address', result.broadcast],
        ['Usable host range', `${result.firstHost} - ${result.lastHost}`],
        ['Usable hosts', result.usableHosts.toLocaleString('en-US')],
        ['Total addresses', result.totalAddresses.toLocaleString('en-US')],
        ['Mask in binary', result.binaryMask],
        ['IP class', result.ipClass],
        ['Address type', result.scope],
      ]
    : [];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Network</span>
        <h2 className="tool-live-title">Subnet calculator</h2>
        <p className="tool-live-subtitle">
          Enter an IPv4 address and a prefix length (or a subnet mask) to get the network,
          broadcast, host range and mask. Works offline.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>IP address (or address/prefix, like 10.0.0.1/8)</span>
            <input
              onChange={(e) => {
                report();
                setAddress(e.target.value);
              }}
              spellCheck={false}
              type="text"
              value={address}
            />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Prefix length or subnet mask</span>
            <input
              onChange={(e) => {
                report();
                setPrefix(e.target.value);
              }}
              spellCheck={false}
              type="text"
              value={prefix}
            />
          </label>
          {error ? <p className="status-message status-error">{error}</p> : null}
        </section>
        <section className="tool-live-preview">
          <div className="tool-html-meta-grid">
            {rows.map(([label, value]) => (
              <div className="tool-live-field" key={label}>
                <span>{label}</span>
                <input readOnly type="text" value={value} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
